
import { useState, useEffect } from 'react';
import { stockDatasets } from '../data/sampleData';
import PredictionModel from './PredictionModel';
import DatasetSelector from './DatasetSelector';
import CorrelationMatrix from './CorrelationMatrix';
import BacktestChart from './BacktestChart';
import CombinedVisualization from './CombinedVisualization';
import BacktestResultsTable from './BacktestResultsTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const PredictionTab = () => {
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([stockDatasets[0].id]);
  const [selectedStocks, setSelectedStocks] = useState(
    stockDatasets.filter(s => selectedDatasets.includes(s.id))
  );
  const [timeframe, setTimeframe] = useState<'1W' | '1M' | '3M' | '6M' | '1Y' | 'All'>('3M');
  const [predictionGenerated, setPredictionGenerated] = useState(false);
  
  useEffect(() => {
    setSelectedStocks(stockDatasets.filter(s => selectedDatasets.includes(s.id)));
  }, [selectedDatasets]);
  
  const handleDatasetSelection = (selectedIds: string[]) => {
    if (selectedIds.length === 0) {
      return;
    }
    setSelectedDatasets(selectedIds);
    setPredictionGenerated(false); // Reset when selection changes
  };
  
  const handlePredictionComplete = () => {
    setPredictionGenerated(true);
    toast.success("Prediction generated successfully!");
  };
  
  const handleTimeframeChange = (newTimeframe: '1W' | '1M' | '3M' | '6M' | '1Y' | 'All') => {
    setTimeframe(newTimeframe);
  };
  
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <div className="text-sm font-medium">Select Datasets for Prediction</div>
        <DatasetSelector 
          onSelectionChange={handleDatasetSelection} 
          selectedDatasets={selectedDatasets}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {selectedStocks.map(stock => (
          <PredictionModel 
            key={stock.id} 
            stockData={stock} 
            onPredictionComplete={handlePredictionComplete}
            externalTimeframe={timeframe}
            onTimeframeChange={handleTimeframeChange}
          />
        ))}
      </div>
      
      {selectedStocks.length > 1 && predictionGenerated && (
        <CombinedVisualization stocksData={selectedStocks} />
      )}
      
      <Tabs defaultValue="correlation" className="w-full">
        <TabsList className="grid grid-cols-3 w-full max-w-md mx-auto mb-6">
          <TabsTrigger value="correlation">Correlation</TabsTrigger>
          <TabsTrigger value="backtest">Backtest</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
        </TabsList>
        
        <TabsContent value="correlation">
          {predictionGenerated ? (
            <CorrelationMatrix stocksData={selectedStocks} />
          ) : (
            <div className="text-center py-12 bg-card border rounded-xl">
              <p className="text-muted-foreground">Generate predictions to view correlation matrix</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="backtest">
          {predictionGenerated ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {selectedStocks.slice(0, 2).map(stock => (
                <BacktestChart key={stock.id} stockData={stock} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-card border rounded-xl">
              <p className="text-muted-foreground">Generate predictions to view backtest results</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="results">
          {predictionGenerated ? (
            <BacktestResultsTable stockData={selectedStocks[0]} />
          ) : (
            <div className="text-center py-12 bg-card border rounded-xl">
              <p className="text-muted-foreground">Generate predictions to view detailed results</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PredictionTab;
